import React, { useState, useEffect } from 'react';
import { Widget, FormSelect, FormSubmitButton, FormatText, FormInput } from '@digital/ui';
import { SelectValue } from 'antd/lib/select';

import { hasPermission } from '@app/helpers/permissions';

import { Usuario } from '@app/api/usuario';
import { Departamento } from '@app/api/departamento';
import { Diretoria } from '@app/api/diretoria';
import { Area } from '@app/api/area';
import { updateResponsaveisCrfProjeto, CrfProjeto } from '@app/api/crf/crfProjeto';

interface FormResponsaveisProps {
    selectedItem: CrfProjeto;
    usuarios: Usuario[];
    diretorias: Diretoria[];
    areas: Area[];
    departamentos: Departamento[];
}

const FormResponsaveisCrfProjeto = (props: FormResponsaveisProps) => {
    const { diretorias, areas, departamentos, selectedItem } = props;
    const [filteredAreas, setFilteredAreas] = useState<Area[]>([]);
    const [filteredDepartamentos, setFilteredDepartamentos] = useState<Departamento[]>([]);
    const [selectedAreaId, setSelectedAreaId] = useState<string>();
    const [selectedDepartamentoId, setSelectedDepartamentoId] = useState<string>();

    useEffect(() => {
        if (selectedItem) {

            setSelectedDepartamentoId(selectedItem.departamentoId);
            setSelectedAreaId(selectedItem.areaId);
            const areasList = areas.filter(a => a.diretoriaId === selectedItem.diretoriaId);
            if (selectedItem.areaId) {
                const departamentosList = departamentos.filter(a => a.areaId === selectedItem.areaId);
                setFilteredDepartamentos(departamentosList);
            } else if (selectedItem.diretoriaId) {
                const newFilteredList = departamentos.filter(d => d.diretoriaId === selectedItem.diretoriaId);
                setFilteredDepartamentos(newFilteredList);
            }

            setFilteredAreas(areasList);
        }
    }, [selectedItem, areas, departamentos]);

    const handleUpdate = () => {
        if (selectedItem) {

            selectedItem.departamentoId = selectedDepartamentoId || '';
            selectedItem.areaId = selectedAreaId || '';
            updateResponsaveisCrfProjeto(selectedItem);
        }
    };

    const handleChangeDiretoria = (value: SelectValue) => {
        if (value) {
            // Reseting Fields to set new IDS
            selectedItem.areaId = '';
            selectedItem.departamentoId = '';
            setSelectedDepartamentoId('');
            setSelectedAreaId('');

            selectedItem.diretoriaId = String(value);
            const areasList = areas.filter(a => a.diretoriaId === value);
            const departamentosList = departamentos.filter(a => a.diretoriaId === value);

            setFilteredAreas(areasList);
            if (departamentosList.length > 0) {
                setFilteredDepartamentos(departamentosList);
            } else {
                setFilteredDepartamentos([]);
            }
        }
    };

    const handleChangeArea = (value: SelectValue) => {

        if (value) {

            // Reseting DepartamentoID to set a new ID according to selected Area
            selectedItem.departamentoId = '';
            setSelectedDepartamentoId('');

            selectedItem.areaId = String(value);
            setSelectedAreaId(String(value));

            const isArea = departamentos.filter(a => a.areaId === selectedItem.areaId).length > 0;

            if (isArea) {
                const newFilteredList = departamentos.filter(d => d.areaId === value);
                setFilteredDepartamentos(newFilteredList);
            } else {
                setFilteredDepartamentos([]);
            }
        } else {
            selectedItem.areaId = '';
            selectedItem.departamentoId = '';
            setSelectedDepartamentoId('');
            setSelectedAreaId('');
            const isDiretoria = departamentos.filter(a => a.diretoriaId === selectedItem.diretoriaId).length > 0;
            if (isDiretoria) {
                const newFilteredList = departamentos.filter(d => d.diretoriaId === selectedItem.diretoriaId);
                setFilteredDepartamentos(newFilteredList);
            } else {
                setFilteredDepartamentos([]);
            }
        }
    };

    const handleChangeDepartamento = (value: SelectValue) => {

        if (value) {
            selectedItem.departamentoId = String(value);
            setSelectedDepartamentoId(String(value));
        } else {
            selectedItem.areaId = '';
            selectedItem.departamentoId = '';
            setSelectedDepartamentoId('');
            setSelectedAreaId('');

            const isDiretoria = departamentos.filter(a => a.diretoriaId === selectedItem.diretoriaId).length > 0;
            if (isDiretoria) {
                const newFilteredList = departamentos.filter(d => d.diretoriaId === selectedItem.diretoriaId);
                setFilteredDepartamentos(newFilteredList);
            } else {
                setFilteredDepartamentos([]);
                setSelectedDepartamentoId('');
            }
        }
    };

    return (
        <Widget
            title="Responsáveis"
            actions={[
                hasPermission(['PROJETO_ABA_GERAL_EDITAR']) && (
                    <FormSubmitButton type="primary" onClick={handleUpdate}>
                        <FormatText id="ui.commons.save" uppercase />
                    </FormSubmitButton>
                ),
            ]}>
            <FormSelect
                label="Diretoria"
                name="diretoriaId"
                options={diretorias && diretorias.map(a => ({ text: a.nome, value: a.id.toString() }))}
                span={4}
                optionFilterProp="children"
                onChange={value => handleChangeDiretoria(value)}
            />
            <FormSelect
                label="Área"
                name="areaId"
                options={filteredAreas && filteredAreas.map(a => ({ text: a.nome, value: a.id.toString() }))}
                span={4}
                optionFilterProp="children"
                onChange={value => handleChangeArea(value)}
                value={selectedAreaId}
                allowClear
            />
            <FormSelect
                label="Departamento"
                name="departamentoId"
                options={filteredDepartamentos && filteredDepartamentos.map(a => ({ text: a.nome, value: a.id.toString() }))}
                span={4}
                optionFilterProp="children"
                onChange={value => handleChangeDepartamento(value)}
                value={selectedDepartamentoId}
                allowClear
            />
            <FormInput label="Data de Cadastro TOTVS" name="dataHoraCadastroTotvsFormatada" span={4} disabled />
            <FormInput label="Responsável pelo Projeto" name="responsavelMatriculaNome" span={4} disabled />
            <FormInput label="Centro de Custo" name="responsavelCentroDeCusto" span={4} disabled />
            <FormInput label="Data de Edição TOTVS" name="ultimaAtualizacaoDataHoraFormatada" span={4} disabled />
            <FormInput label="Responsável pela Edição" name="ultimaAtualizacaoMatriculaResponsavelNome" span={4} disabled />
        </Widget>
    );
};

export default FormResponsaveisCrfProjeto;
